import {
  type MutableRefObject,
  type ReactNode,
  createContext,
  useContext,
} from "react";
import { useTimelineScrollAnim } from "../hooks/use-timeline-scroll-anim";
import type { ITimelineEditorData } from "../types";

interface ITimelineScrollData {
  scrollTop: number;
}

export interface ITimelineScrollListener {
  id: string;
  callback: (data: ITimelineScrollData) => void;
}

export type ITimelinePathUpdateData = {};

export interface ITimelinePathUpdateListener {
  id: string;
  callback: (data: ITimelinePathUpdateData) => void;
  target: Element;
}

export interface ITimelineScrollAnimCtx {
  canAnimate: MutableRefObject<boolean>;
  scale: MutableRefObject<{ x: number; y: number }>;

  addScrollListener: (
    callback: ITimelineScrollListener["callback"],
  ) => () => void;
  addPathUpdateListener: (
    target: Element,
    callback: ITimelinePathUpdateListener["callback"],
  ) => () => void;
}

const initState: ITimelineScrollAnimCtx = {
  canAnimate: { current: true },
  scale: { current: { x: 1, y: 1 } },
  addScrollListener: () => () => {},
  addPathUpdateListener: () => () => {},
};

export const TimelineAnimationCtx =
  createContext<ITimelineScrollAnimCtx>(initState);

interface IProps {
  children: ReactNode;
  containerRef: MutableRefObject<HTMLDivElement | null>;
  editorData?: ITimelineEditorData;
}

export const TimelineScrollAnimCtxProvider = ({
  children,
  containerRef,
  editorData,
}: IProps) => {
  const ctxState = useTimelineScrollAnim({
    editorData,
    containerRef,
  });

  return (
    <TimelineAnimationCtx.Provider value={ctxState}>
      {children}
    </TimelineAnimationCtx.Provider>
  );
};

export const useTimelineScrollAnimCtx = () => {
  const ctx = useContext(TimelineAnimationCtx);
  if (!ctx)
    throw Error(
      "useTimelineScrollAnimCtx: TimelineAnimationCtx is not defined!",
    );

  return { ...ctx };
};
